import { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { FiStar } from "react-icons/fi";
import { apiUrl } from "../../config/api";
import { deleteProperty } from "../../services/propertiesService";

const MisPublicaciones = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState("");
  const [statusBusy, setStatusBusy] = useState("");

  const statusOptions = (property) => [
    ['disponible','Disponible'],
    ['reservado','Reservada'],
    ['pausado','Pausada'],
    ...(property.operation === 'venta' ? [['vendido','Vendida']] : [['alquilado','Alquilada']]),
  ];

  const handleStatus = async (property, status) => {
    if (!status || status === (property.status || 'disponible')) return;
    const final = ['vendido','alquilado'].includes(status);
    const copy = final
      ? `¿Marcar “${property.title}” como ${status === 'vendido' ? 'vendida' : 'alquilada'}? Dejará de aparecer en resultados, avisaremos a quienes la guardaron y, si corresponde, detendremos la renovación de tu suscripción.`
      : status === 'pausado'
        ? `¿Pausar “${property.title}”? Dejará de aparecer en resultados y liberará el cupo hasta que la reactives.`
        : `¿Cambiar el estado de “${property.title}”?`;
    if (!window.confirm(copy)) return;
    setStatusBusy(property._id); setFeedback('');
    try {
      const res = await fetch(apiUrl(`/properties/${property._id}/status`), {
        method:'PATCH', credentials:'include', headers:{ 'Content-Type':'application/json' }, body:JSON.stringify({ status }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message || 'No pudimos actualizar el estado');
      setProperties(current => current.map(item => item._id === property._id ? { ...item, ...data.property, status } : item));
      setFeedback(data.message || 'Estado actualizado correctamente.');
    } catch (error) { setFeedback(error.message || 'No pudimos actualizar el estado.'); }
    finally { setStatusBusy(''); }
  };

  const handleDelete = async (property) => {
    if (!window.confirm(`¿Eliminar “${property.title}”? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteProperty(property._id);
      setProperties((current) => current.filter((item) => item._id !== property._id));
      setFeedback("La publicación fue eliminada.");
    } catch (error) {
      setFeedback(error.message || "No pudimos eliminar la publicación.");
    }
  };

  const handleFeature = async (property) => {
    if (!property?._id) return;
    if (property.isPromoted && property.featuredUntil) {
      const featuredDate = new Date(property.featuredUntil);
      if (!Number.isNaN(featuredDate.getTime()) && featuredDate > new Date()) {
        setFeedback("Esta propiedad ya está destacada.");
        return;
      }
    }

    try {
      const res = await fetch(apiUrl(`/properties/${property._id}/feature`), {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        if (res.status === 403 && data?.code === 'FEATURED_CREDIT_REQUIRED') {
          navigate(`/client/planes?intent=feature&property=${encodeURIComponent(property._id)}`);
          return;
        }
        throw new Error(data?.message || 'No pudimos destacar la propiedad');
      }

      setProperties((current) => current.map((item) => item._id === property._id ? {
        ...item,
        isFeatured: true,
        isPromoted: true,
        promotionTier: data?.property?.promotionTier || 'DESTACADA',
        featuredUntil: data?.property?.featuredUntil || item.featuredUntil,
      } : item));
      setFeedback(data?.message || 'La propiedad quedó destacada.');
    } catch (error) {
      setFeedback(error.message || 'No pudimos destacar la propiedad.');
    }
  };

  useEffect(() => {
    const fetchMyProperties = async () => {
      try {
        const res = await fetch(
          apiUrl('/properties/mine'),
          {
            credentials: 'include',
          }
        );

        if (!res.ok) {
          throw new Error("Error obteniendo propiedades");
        }

        const data = await res.json();
        setProperties(data);
      } catch (err) {
        console.error("Error cargando publicaciones:", err);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchMyProperties();
    }
  }, [user]);

  if (loading) {
    return <Centered>Cargando publicaciones...</Centered>;
  }

  return (
    <Wrapper>
      <Container>
        <Header>
          <Title>Mis publicaciones</Title>
          <Subtitle>
            Gestioná todas tus propiedades publicadas.
          </Subtitle>
        </Header>
        {feedback && <Feedback role="status">{feedback}</Feedback>}

        {properties.length === 0 ? (
          <EmptyState>
            <h3>No tenés propiedades publicadas</h3>
            <p>Cuando publiques una propiedad aparecerá acá.</p>
            <PublishButton to="/publicar">
              Publicar propiedad
            </PublishButton>
          </EmptyState>
        ) : (
          <Grid>
            {properties.map((property) => (
              <Card key={property._id}>
                <ImageWrapper>
                  <StatusBadge $status={property.status || 'disponible'}>{statusOptions(property).find(([value])=>value===(property.status||'disponible'))?.[1] || property.status}</StatusBadge>
                  {property.images?.length ? (
                    <img
                      src={property.images[0]}
                      alt={property.title}
                    />
                  ) : (
                    <Placeholder />
                  )}
                </ImageWrapper>

                <CardContent>
                  <Price>
                    {property.operation === "venta"
                      ? "Venta"
                      : "Alquiler"}{" "}
                    · {property.currency === "ARS" ? "$" : "US$"} {Number(property.price || 0).toLocaleString("es-AR")}
                  </Price>

                  <PropertyTitle>{property.title}</PropertyTitle>

                  <Location>
                    {property.department}{" "}
                    {property.neighborhood}
                  </Location>

                  <Meta>
                    {(property.surfaceCovered || property.surfaceTotal) > 0 && (
                      <span>{property.surfaceCovered || property.surfaceTotal} m²</span>
                    )}
                    {property.rooms && (
                      <span>{property.rooms} amb.</span>
                    )}
                    {property.bathrooms && (
                      <span>{property.bathrooms} baños</span>
                    )}
                    {property.pricePerSquareMeter > 0 && (
                      <span>{property.currency === "ARS" ? "$" : "US$"} {Number(property.pricePerSquareMeter).toLocaleString("es-AR")} / m²</span>
                    )}
                  </Meta>
                  {property.showPriceReduction !== false && property.priceReductionPercentage > 0 && <Reduction>BAJÓ {property.priceReductionPercentage}%</Reduction>}

                  <StatusControl>
                    <label htmlFor={`status-${property._id}`}>Estado de la publicación</label>
                    <select id={`status-${property._id}`} value={property.status || 'disponible'} disabled={statusBusy===property._id} onChange={event=>handleStatus(property,event.target.value)}>
                      {statusOptions(property).map(([value,label])=><option key={value} value={value}>{label}</option>)}
                    </select>
                    <small>{statusBusy===property._id?'Actualizando y enviando avisos…':['disponible','reservado'].includes(property.status||'disponible')?'Visible o gestionable como publicación activa.':'No aparece en los resultados y no ocupa un cupo activo.'}</small>
                  </StatusControl>

                  <Actions>
                    <EditLink
                      to={`/client/properties/${property._id}/edit`}
                    >
                      Editar
                    </EditLink>

                    <ViewLink
                      to={`/properties/${property._id}`}
                    >
                      Ver
                    </ViewLink>

                    {!property.isPromoted && (property.status || 'disponible') === 'disponible' && (
                      <FeatureButton type="button" onClick={() => handleFeature(property)}>
                        <FiStar /> Destacar
                      </FeatureButton>
                    )}

                    <DeleteButton type="button" onClick={() => handleDelete(property)}>Eliminar</DeleteButton>
                  </Actions>
                </CardContent>
              </Card>
            ))}
          </Grid>
        )}
      </Container>
    </Wrapper>
  );
};

export default MisPublicaciones;

/* ================= STYLES ================= */

const Wrapper = styled.div`
  padding: 60px 20px 80px;
  @media(max-width:600px){padding:36px 14px 70px;}
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 36px;
`;
const Feedback = styled.div`margin:-18px 0 28px;padding:14px 18px;color:#29474f;background:#e8efeb;border-radius:10px;`;

const Title = styled.h1`
  font-size: 28px;
  margin-bottom: 6px;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 15px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 28px;
  @media(max-width:600px){grid-template-columns:minmax(0,1fr);gap:18px;}
`;

const Card = styled.div`
  background: white;
  border: 1px solid #dce4e3;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(42,64,70,0.055);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

const ImageWrapper = styled.div`
  position:relative;
  height: 200px;
  background: #f1f1f1;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  background: #eaeaea;
`;
const StatusBadge=styled.span`position:absolute;z-index:2;top:12px;left:12px;padding:8px 11px;border-radius:999px;color:${p=>p.$status==='disponible'?'#285d47':'#fff'};background:${p=>p.$status==='disponible'?'#e4f2e8':p.$status==='reservado'?'#b87835':p.$status==='pausado'?'#607175':'#9c4d38'};box-shadow:0 7px 18px rgba(30,49,54,.18);font-size:12px;font-weight:850;letter-spacing:.05em;text-transform:uppercase;`;

const CardContent = styled.div`
  padding: 22px;
  @media(max-width:600px){padding:19px 17px;}
`;

const Price = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #c66f4e;
  margin-bottom: 6px;
`;

const PropertyTitle = styled.h3`
  font-size: 16px;
  margin-bottom: 6px;
`;

const Location = styled.div`
  font-size: 13px;
  color: #777;
  margin-bottom: 10px;
`;

const Meta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #666;
`;
const Reduction = styled.span`display:inline-block;margin-top:12px;padding:6px 9px;color:#9a4d34;background:#f8e9e2;border-radius:999px;font-size:12px;font-weight:800;letter-spacing:.06em;`;
const StatusControl=styled.div`display:grid;gap:7px;margin-top:18px;padding:14px;background:#f5f5f0;border-radius:14px;label{color:#29474f;font-size:13px;font-weight:800}select{width:100%;height:48px;padding:0 40px 0 13px;border:1px solid #d4dcda;border-radius:12px;background:#fff;color:#29474f;font:inherit;font-size:16px;font-weight:700}select:focus{outline:none;border-color:#c66f4e;box-shadow:0 0 0 3px rgba(198,111,78,.12)}small{color:#6e7d7f;font-size:12px;line-height:1.4}`;

const Actions = styled.div`
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items:center;
  @media(max-width:480px){display:grid;grid-template-columns:repeat(2,minmax(0,1fr));a,button{min-height:42px;display:flex;align-items:center;justify-content:center;padding:0 10px;border:1px solid #dce3e1;border-radius:999px!important;}}
`;

const EditLink = styled(Link)`
  font-size: 13px;
  color: #c66f4e;
  text-decoration: none;
`;

const ViewLink = styled(Link)`
  font-size: 13px;
  color: #333;
  text-decoration: none;
`;
const FeatureButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  color: #c66f4e;
  background: transparent;
  border: 0;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
`;
const DeleteButton = styled.button`padding:0;color:#9b3f32;background:transparent;border:0;font:inherit;font-size:13px;cursor:pointer;`;

const EmptyState = styled.div`
  text-align: center;
  margin: 120px auto;
  max-width: 500px;

  h3 {
    margin-bottom: 8px;
  }

  p {
    color: #666;
  }
`;

const PublishButton = styled(Link)`
  display: inline-block;
  margin-top: 20px;
  background: #c66f4e;
  color: white;
  padding: 12px 26px;
  border-radius: 999px;
  text-decoration: none;
`;

const Centered = styled.div`
  text-align: center;
  margin-top: 120px;
`;
